import { Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy, DestroyRef, inject, ViewChild, ElementRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { debounceTime } from 'rxjs/operators';

import { Subscription } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { GuestDetailPage } from 'src/pages/my-pros/guest-detail/guest-detail';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface GuestContact {
  name: string;
  email: string;
  phone: string;
  properties: string[];
  stays: number;
  source: string;
}

@Component({
  selector: 'guests-tab',
  templateUrl: './guests-tab.component.html',
  styleUrls: ['./guests-tab.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GuestsTabComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private client = inject(Client);
  private guestReservations = inject(GuestReservations);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private aboutThisPageService = inject(AboutThisPageService);
  private onboardingDummyDataService = inject(OnboardingDummyDataService);
  private readonly demoBlock = inject(DemoBlockService);
  readonly windowService = inject(WindowService);

  searchForm: UntypedFormGroup;
  headers: string[] = ['Name', 'Email', 'Phone', 'Properties', 'Stays', 'Source', 'last-column'];
  allRows: any[] = [];
  shownRows: any[] = [];
  loaded = false;
  errorMessage = '';
  addressResponse: any[] = [];
  showAboutThisPage = false;
  overlayTop: string;
  private _guestsHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('guestsHeader', { read: ElementRef }) set guestsHeader(el: ElementRef) {
    this._guestsHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }
  @ViewChild('tidyTable') tidyTable: any;

  constructor() {
    this.searchForm = this.fb.group({ search: [''] });
    this.searchForm.get('search')?.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => this.updateSearch(value));
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('guests');
  }

  async ngOnInit() {
    try {
      if (this.showAboutThisPage) {
        this.loadMockData();
        return;
      }
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load guests';
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  loadMockData() {
    const contacts = this.onboardingDummyDataService.getGuestTenantsMockData();
    this.headers = ['Name', 'Email', 'Phone', 'Properties', 'Stays', 'Source', 'Last Stay', 'last-column'];
    this.allRows = contacts.map(contact => ({
      'Name': { value: contact.name, initials: true },
      'Email': { value: contact.email },
      'Phone': { value: contact.phone },
      'Properties': (contact.properties || []).filter(Boolean).map((p: string) => ({ value: p, action: null })),
      'Stays': { value: String(contact.stays) },
      'Source': { value: contact.source },
      'Last Stay': { value: contact.last_stay ? new ParseDateTimePipe().transform(contact.last_stay, 'MMM d, yyyy') : '' },
      'last-column': { value: '', hasChevron: true },
    }));
    this.shownRows = [...this.allRows];
    this.loaded = true;
    this.cdr.markForCheck();
  }

  async loadPage() {
    this.addressResponse = await this.client.getAddresses();

    const [reservationsResponse, sources] = await Promise.all([
      this.guestReservations.getReservations({ per_page: 500 }),
      this.guestReservations.getReservationSource(),
    ]);

    const reservations = reservationsResponse.body;
    const contacts = this.deduplicateGuests(reservations, sources);

    this.headers = ['Name', 'Email', 'Phone', 'Properties', 'Stays', 'Source', 'last-column'];
    this.allRows = this.buildRows(contacts);
    this.shownRows = [...this.allRows];
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._guestsHeader) {return;}
      const top = this._guestsHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
  }

  async dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.overlayResizeObserver = null;
    this.allRows = [];
    this.shownRows = [];
    this.loaded = false;
    this.cdr.markForCheck();
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load guests';
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  private deduplicateGuests(reservations: any[], sources: any[]): GuestContact[] {
    const contactMap = new Map<string, GuestContact>();

    for (const res of reservations) {
      if (!res.guest_name && !res.guest_email) {
        continue;
      }

      const key = (res.guest_email || res.guest_name || '').toLowerCase().trim();
      if (!key) {
        continue;
      }

      const propertyName = this.getPropertyName(res.guest_reservation_source_id, sources);

      if (contactMap.has(key)) {
        const existing = contactMap.get(key);
        existing.stays++;
        if (!existing.email && res.guest_email) {
          existing.email = res.guest_email;
        }
        if (!existing.phone && res.guest_phone) {
          existing.phone = res.guest_phone;
        }
        if (propertyName && !existing.properties.includes(propertyName)) {
          existing.properties.push(propertyName);
        }
      } else {
        contactMap.set(key, {
          name: res.guest_name || '',
          email: res.guest_email || '',
          phone: res.guest_phone || '',
          properties: propertyName ? [propertyName] : [],
          stays: 1,
          source: res.source || '',
        });
      }
    }

    return Array.from(contactMap.values());
  }

  private getPropertyName(sourceId: number, sources: any[]): string {
    if (!sourceId) {
      return '';
    }
    const source = sources.find(s => s.id === sourceId);
    if (!source?.address_id) {
      return '';
    }
    const address = this.addressResponse.find(a => a.id === source.address_id);
    return address?.address_name || address?.address1 || '';
  }

  private buildRows(contacts: GuestContact[]): any[] {
    return contacts.map(contact => ({
      'Name': { value: contact.name, initials: true },
      'Email': { value: contact.email },
      'Phone': { value: contact.phone },
      'Properties': contact.properties.map(p => ({ value: p, action: null })),
      'Stays': { value: String(contact.stays) },
      'Source': { value: contact.source },
      'last-column': { value: '', hasChevron: true },
    }));
  }

  updateSearch(search: string) {
    if (!search || search.length < 2) {
      this.shownRows = [...this.allRows];
      return;
    }
    const term = search.toLowerCase();
    this.shownRows = this.allRows.filter(row =>
      row['Name'].value.toLowerCase().includes(term) ||
      row['Email'].value.toLowerCase().includes(term) ||
      row['Phone'].value.toLowerCase().includes(term)
    );
  }

  sortChanged(sort: any) {
    if (!sort.active || !sort.direction) {
      return;
    }
    this.shownRows = [...this.shownRows].sort((a, b) => {
      const valA = a[sort.active]?.value || '';
      const valB = b[sort.active]?.value || '';
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) { return sort.direction === 'asc' ? -1 : 1; }
      if (strA > strB) { return sort.direction === 'asc' ? 1 : -1; }
      return 0;
    });
  }

  goToWifiMarketing() {
    if (this.demoBlock.block()) { return; }
    this.navCtrl.navigateForward('revenue?tab=wifi');
  }

  onRowClick(event: { column: string; element: any }) {
    this.openContact(event.element);
  }

  openContact(row: any) {
    if (!row) { return; }
    const contact = {
      name: row['Name']?.value || '',
      email: row['Email']?.value || '',
      phone: row['Phone']?.value || '',
      properties: (row['Properties'] || []).map((p: any) => p?.value).filter(Boolean),
      stays: Number(row['Stays']?.value) || 0,
      source: row['Source']?.value || '',
    };
    this.goToPage('guest-detail', { contact }, GuestDetailPage);
  }

  private panelClosedSubscription: Subscription;

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.cdr.markForCheck());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
